import { Box, Text, Input, Button, VStack, FormControl, FormLabel, FormHelperText } from '@chakra-ui/react';
import Card from 'components/card/Card';
import { AttachmentIcon } from '@chakra-ui/icons';
import FileUploadCard from 'components/FileUploadCard';
import useFormBinderDisabled from 'hooks/useFormBinderDisabled';
import { useAppSelector } from 'hooks';
import { FILE_TYPE } from 'variables/constants';
import type { APIApplicationFile } from 'types/api.types';

interface AdditionalDocumentsSectionProps {
  uploadedFiles: APIApplicationFile[];
  onFilesUpload: (files: File[], context?: { fileType?: string }) => Promise<void>;
  onFileRemove: (fileId: number, context?: { fileType?: string }) => void;
  isUploading: boolean;
  isDeletingFileId?: number | null;
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
}

const AdditionalDocumentsSection = ({
  uploadedFiles,
  onFilesUpload,
  onFileRemove,
  isUploading,
  isDeletingFileId = null,
  formStateKey,
}: AdditionalDocumentsSectionProps) => {
  const isDisabled = useFormBinderDisabled(formStateKey);

  // Detect carrier requirements
  const data = useAppSelector((state) => state[formStateKey].data);
  const insuranceCompany = formStateKey === 'autoPersonalForm'
    ? (data as any)?.company_and_broker_information?.insurance_company
    : (data as any)?.policy_information?.insurance_company;

  const isFacilityCarrier = insuranceCompany?.toLowerCase().includes('facility');

  // Filter files by type
  const regularAdditionalFiles = uploadedFiles.filter(
    (file) => file.file_type !== FILE_TYPE.TYPE_FACILITY_REFUSAL_LETTER_FOR_AUTO.type
  );

  const signedRefusalFiles = uploadedFiles.filter(
    (file) => file.file_type === FILE_TYPE.TYPE_FACILITY_REFUSAL_LETTER_FOR_AUTO.type
  );
  
  const isSignedRefusalRequired = formStateKey === 'autoPersonalForm' && isFacilityCarrier;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      await onFilesUpload(fileArray);
    }
    e.target.value = '';
  };

  const handleSignedRefusalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      await onFilesUpload(fileArray, { fileType: FILE_TYPE.TYPE_FACILITY_REFUSAL_LETTER_FOR_AUTO.type.toString() });
    }
    e.target.value = '';
  };

  return (
    <Card mb={4}>
      <Box>
        {/* Signed Refusal Letter Section (when Facility carrier) */}
        {isSignedRefusalRequired && (
          <FormControl isRequired mb={3}>
            <FormLabel fontSize="md" fontWeight="bold">
              Signed Refusal Letter
            </FormLabel>
            <FormHelperText mb={3}>
              Facility carrier requires a signed refusal letter to complete the submission.
            </FormHelperText>

            <Box mb={3}>
              <Input
                id="signed-refusal-upload"
                type="file"
                display="none"
                onChange={handleSignedRefusalUpload}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                disabled={isUploading || isDisabled}
              />
              <label htmlFor="signed-refusal-upload">
                <Button
                  as="span"
                  leftIcon={<AttachmentIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                  isLoading={isUploading}
                  loadingText="Uploading..."
                  cursor="pointer"
                  isDisabled={isDisabled}
                >
                  Upload Signed Refusal Letter
                </Button>
              </label>
              <Text fontSize="xs" color="gray.500" mt={2}>
              Supported: PDF, Images, Excel, Word
            </Text>
            </Box>

            {signedRefusalFiles.length > 0 && (
              <VStack align="stretch" spacing={3}>
                {signedRefusalFiles.map((file) => (
                  <FileUploadCard
                    key={file.id}
                    file={file}
                    badgeText="Signed Refusal Letter"
                    // badgeColorScheme="red"
                    onDelete={() => onFileRemove(file.id, { fileType: FILE_TYPE.TYPE_FACILITY_REFUSAL_LETTER_FOR_AUTO.type.toString() })}
                    isDeleting={isDeletingFileId === file.id}
                    isLocked={isDisabled}
                  />
                ))}
              </VStack>
            )}
          </FormControl>
        )}

        {/* Regular Additional Documents Section */}
        <Box>
          <Text fontWeight="bold" fontSize="md" mb={2}>
            Additional Documents
          </Text>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Recommended: Ownership, Photos of Vehicle, Vehicle Appraisal
          </Text>

          <Box mb={3}>
            <Input
              id="additional-file-upload"
              type="file"
              multiple
              display="none"
              onChange={handleFileChange}
              accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.doc,.docx"
              disabled={isUploading || isDisabled}
            />
            <label htmlFor="additional-file-upload">
              <Button
                as="span"
                leftIcon={<AttachmentIcon />}
                colorScheme="blue"
                variant="outline"
                size="md"
                isLoading={isUploading}
                loadingText="Uploading..."
                cursor="pointer"
                width="full"
                maxW="300px"
                isDisabled={isDisabled}
              >
                Choose Files
              </Button>
            </label>
            <Text fontSize="xs" color="gray.500" mt={2}>
              Supported: PDF, Images, Excel, Word
            </Text>
          </Box>

          {regularAdditionalFiles.length > 0 && (
            <VStack align="stretch" spacing={3}>
              {regularAdditionalFiles.map((file) => (
                <FileUploadCard
                  key={file.id}
                  file={file}
                  badgeText="Additional Document"
                  onDelete={() => onFileRemove(file.id)}
                  isDeleting={isDeletingFileId === file.id}
                  isLocked={isDisabled}
                />
              ))}
            </VStack>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default AdditionalDocumentsSection;
